import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { Protected } from "@/components/Protected";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Roster from "@/pages/Roster";
import Stats from "@/pages/Stats";
import Standings from "@/pages/Standings";
import Press from "@/pages/Press";
import TicketsTransfer from "@/pages/TicketsTransfer";
import Coaches from "@/pages/Coaches";
import Schedule from "@/pages/Schedule";
import News from "@/pages/News";
import NewsDetail from "@/pages/NewsDetail";
import Gallery from "@/pages/Gallery";
import Sponsors from "@/pages/Sponsors";
import Contact from "@/pages/Contact";
import Tryouts from "@/pages/Tryouts";
import TryoutSuccess from "@/pages/TryoutSuccess";
import Tickets from "@/pages/Tickets";
import TicketsSuccess from "@/pages/TicketsSuccess";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import { AdminPlayers, AdminCoaches, AdminGames, AdminNews, AdminSponsors, AdminGallery } from "@/pages/admin/AdminCRUD";
import AdminTryouts from "@/pages/admin/AdminTryouts";
import { AdminContact, AdminNewsletter, AdminPressInquiries } from "@/pages/admin/AdminInbox";
import { AdminTicketTiers, AdminTicketOrders } from "@/pages/admin/AdminTickets";
import AdminSponsorLeads from "@/pages/admin/AdminSponsorLeads";
import AdminCheckin from "@/pages/admin/AdminCheckin";
import AdminCheckinLogs from "@/pages/admin/AdminCheckinLogs";
import AdminBoxScores from "@/pages/admin/AdminBoxScores";
import { AdminStandings, AdminPressAssets, AdminMediaInquiries } from "@/pages/admin/AdminPressStandings";
import AdminSettings from "@/pages/admin/AdminSettings";
import Store from "@/pages/Store";
import ProductDetail from "@/pages/ProductDetail";
import StoreCheckout from "@/pages/StoreCheckout";
import StoreSuccess from "@/pages/StoreSuccess";
import { AdminProducts, AdminStoreOrders } from "@/pages/admin/AdminStore";
import AdminDiscountCodes from "@/pages/admin/AdminDiscountCodes";
import { CartProvider } from "@/contexts/CartContext";

function AppRouter() {
    const location = useLocation();
    // Detect OAuth fragment FIRST, synchronously during render
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            {/* Admin (no public layout) */}
            <Route
                path="/admin"
                element={<Protected adminOnly><AdminLayout /></Protected>}
            >
                <Route index element={<AdminOverview />} />
                <Route path="players" element={<AdminPlayers />} />
                <Route path="coaches" element={<AdminCoaches />} />
                <Route path="schedule" element={<AdminGames />} />
                <Route path="news" element={<AdminNews />} />
                <Route path="gallery" element={<AdminGallery />} />
                <Route path="sponsors" element={<AdminSponsors />} />
                <Route path="tryouts" element={<AdminTryouts />} />
                <Route path="tickets/tiers" element={<AdminTicketTiers />} />
                <Route path="tickets/orders" element={<AdminTicketOrders />} />
                <Route path="checkin" element={<AdminCheckin />} />
                <Route path="checkin/logs" element={<AdminCheckinLogs />} />
                <Route path="box-scores" element={<AdminBoxScores />} />
                <Route path="standings" element={<AdminStandings />} />
                <Route path="press-assets" element={<AdminPressAssets />} />
                <Route path="media-inquiries" element={<AdminMediaInquiries />} />
                <Route path="sponsor-leads" element={<AdminSponsorLeads />} />
                <Route path="contact" element={<AdminContact />} />
                <Route path="press" element={<AdminPressInquiries />} />
                <Route path="newsletter" element={<AdminNewsletter />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="store-orders" element={<AdminStoreOrders />} />
                <Route path="discount-codes" element={<AdminDiscountCodes />} />
            </Route>

            {/* Public layout */}
            <Route path="/*" element={<PublicShell />} />
        </Routes>
    );
}

function PublicShell() {
    return (
        <Layout>
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/roster" element={<Roster />} />
                <Route path="/stats" element={<Stats />} />
                <Route path="/standings" element={<Standings />} />
                <Route path="/press" element={<Press />} />
                <Route path="/tickets/transfer" element={<TicketsTransfer />} />
                <Route path="/staff" element={<Coaches />} />
                <Route path="/schedule" element={<Schedule />} />
                <Route path="/news" element={<News />} />
                <Route path="/news/:slug" element={<NewsDetail />} />
                <Route path="/gallery" element={<Gallery />} />
                <Route path="/sponsors" element={<Sponsors />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/tryouts" element={<Tryouts />} />
                <Route path="/tryouts/success" element={<TryoutSuccess />} />
                <Route path="/tickets" element={<Tickets />} />
                <Route path="/tickets/success" element={<TicketsSuccess />} />
                <Route path="/store" element={<Store />} />
                <Route path="/store/checkout" element={<StoreCheckout />} />
                <Route path="/store/success" element={<StoreSuccess />} />
                <Route path="/store/:slug" element={<ProductDetail />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Layout>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <SettingsProvider>
                        <CartProvider>
                            <AppRouter />
                            <Toaster position="bottom-right" theme="dark" toastOptions={{ style: { background: "#131316", border: "1px solid #262626", color: "#fff", borderRadius: 0 } }} />
                        </CartProvider>
                    </SettingsProvider>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
